import { SignOut, User } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { useLogout } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Shared VELUX-styled layout for the admin section. Provides a consistent top
 * bar (wordmark + section nav + profile/logout), a warm `bg-background` canvas,
 * and a centered `max-w-5xl` content container so every admin page lines up.
 */

/*
 * Primary actions across admin are a plain `<Button>` on its default variant —
 * brand Yellow with black type, the same CTA the flow uses. There is no admin
 * override class: a black capsule reads as structure, not as "this moves you
 * forward", so the one that used to live here is gone.
 */

const NAV_LINKS = [
  { to: "/admin/households", label: "Households" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/activity", label: "Activity" },
];

interface AdminShellProps {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function AdminShell({ title, actions, children }: AdminShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border sticky top-0 z-10 border-b bg-white">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-2 px-4 sm:gap-6 sm:px-6">
          <Link to="/admin/households" className="flex shrink-0 flex-col justify-center gap-0.5">
            <span className="type-label text-muted">VELUX</span>
            <span className="text-primary text-[15px] leading-4 font-semibold">Smart Climate</span>
          </Link>
          <nav
            className="flex min-w-0 flex-1 [scrollbar-width:none] items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Admin sections"
          >
            {NAV_LINKS.map((link) => {
              const active = location.pathname.startsWith(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    /* "Current section" is structure, so the active chip stays
                       brand Black — yellow is reserved for actions. */
                    "focus-visible:outline-ring shrink-0 rounded-md px-3 py-1.5 text-[15px] font-medium whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-2",
                    active
                      ? "bg-accent/10 text-accent"
                      : "text-muted hover:text-primary hover:bg-background",
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex shrink-0 items-center gap-1">
            <Link
              to="/profile"
              aria-label="Profile"
              className="text-muted hover:text-primary hover:bg-background focus-visible:outline-ring flex size-9 items-center justify-center rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <User className="size-5" weight="light" />
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sign out"
              className="text-muted hover:text-primary hover:bg-background focus-visible:outline-ring flex size-9 items-center justify-center rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <SignOut className="size-5" weight="light" />
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-primary text-[32px] leading-10 font-semibold tracking-[-0.32px]">
            {title}
          </h1>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
        {children}
      </main>
    </div>
  );
}
